import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { initTheme } from "@/lib/theme";

initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// The static #eq-splash in index.html is a child of #root — createRoot().render() replaces
// #root's children atomically on commit, so it's removed by React itself the moment the
// first paint is ready. Removing it manually here left a gap between "splash gone" and
// "React painted" with nothing but the raw <body> background showing through, which is what
// caused the white flash on refresh — so that removal was deleted, not the splash itself.
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// No forced reload on a new service worker taking over. That used to fire
// window.location.reload() on every "controllerchange" — but this SW is network-only
// (it doesn't cache the app shell, see sw.js) and exists purely for push notifications,
// so there's nothing stale to displace. The old behavior could also fire more than once
// per page load if the dev server's SW bytes weren't byte-stable across hot reloads,
// producing a reload loop that looked exactly like a "stuck" splash screen — the app
// picks up the new SW naturally on the next real navigation instead.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => reg.update()).catch(() => {});
  });
}
